import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'wouter';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { AssetListItem } from '../lib/asset-types';
import { useSelection } from '../lib/selection-context';
import { useScrollContainerRef } from '../lib/layout-context';
import { formatTagsOverflow } from '../lib/format-overflow';
import { defaultDirFor, type SortKey, type SortDir } from '../lib/parse-sort';
import type { ListDensity } from '../lib/list-density';
import { HighlightedText } from './HighlightedText';
import { FavoriteStarButton } from './FavoriteStarButton';
import { I } from '../lib/icons';
import { PREFETCH_THRESHOLD_ROWS } from '../../shared/asset-constants';

// Initial-Schätzung der Zeilenhöhe (padding-basiert, uniform). `measureElement`
// korrigiert nach dem ersten Paint. Overscan großzügig (8), damit der Prefetch
// genug Vorlauf hat (eine Tabellenzeile = ein Item, kein Reihen-Bündel).
const ESTIMATED_ROW_HEIGHT = 52;
const TABLE_OVERSCAN = 8;
const TABLE_COLSPAN = 7;

// Helper: relative-time format (Duplikat aus AssetCard — bewusst kein
// Shared-Primitive im Sprint A, siehe spec-listenansicht.md "AssetCard-
// Refactor temptation").
function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'eben';
  const min = Math.floor(sec / 60);
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} T`;
  return new Date(iso).toLocaleDateString('de-DE');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function extBadge(filename: string, mime: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot >= 0) {
    const ext = filename.slice(dot + 1).toUpperCase();
    if (ext.length <= 4) return ext;
  }
  const mimeMap: Record<string, string> = {
    'image/jpeg': 'JPG',
    'image/png': 'PNG',
    'image/webp': 'WEBP',
    'image/gif': 'GIF',
    'image/avif': 'AVIF',
    'image/svg+xml': 'SVG',
    'video/mp4': 'MP4',
    'video/quicktime': 'MOV',
    'application/pdf': 'PDF',
  };
  return mimeMap[mime.toLowerCase()] ?? 'FILE';
}

const FALLBACK_BG: Record<AssetListItem['assetType'], { bg: string; fg: string }> = {
  img: { bg: 'var(--color-brand-green-200)', fg: 'var(--color-brand-green-800)' },
  video: { bg: 'var(--color-asset-video-bg)', fg: 'var(--color-brand-yellow)' },
  pdf: { bg: 'var(--color-asset-pdf-bg)', fg: 'var(--color-brand-green-700)' },
  logo: { bg: 'var(--color-brand-green-700)', fg: 'var(--color-brand-yellow)' },
};

const TYPE_LABEL: Record<AssetListItem['assetType'], string> = {
  img: 'Bild',
  video: 'Video',
  pdf: 'PDF',
  logo: 'Logo',
};

// Density-Mapping: Padding für Row-Cells + Thumbnail-Größe. Font-Sizes bleiben
// stabil über alle Density-Stufen (kein font-shrink — A11y).
const ROW_PADDING: Record<ListDensity, string> = {
  compact: 'py-1.5',
  regular: 'py-2',
  spacious: 'py-3.5',
};
const THUMB_SIZE: Record<ListDensity, string> = {
  compact: 'w-7 h-7',
  regular: 'w-9 h-9',
  spacious: 'w-10 h-10',
};
// Density-Ramp (parametrischer Regler, KEIN Skalen-Token): drei bewusst
// distinkte Mini-Größen für das Thumbnail-Fallback-Glyph je Listendichte.
// Bewusst arbitrary `text-[Npx]` statt der `2xs`/`xs2`-Tokens — würde man sie
// auf ein Token runden, wären compact/regular/spacious visuell identisch und
// der Dichte-Regler verlöre seine Stufung. (Analog asset-fallback-Farben.)
const THUMB_FONT_SIZE: Record<ListDensity, string> = {
  compact: 'text-[8px]',
  regular: 'text-[9px]',
  spacious: 'text-[10px]',
};

type Props = {
  assets: AssetListItem[];
  sort: SortKey;
  dir: SortDir;
  onSortChange: (key: SortKey, dir: SortDir) => void;
  density: ListDensity;
  /** Stapel-Modus: Klick auf eine Stapel-Zeile (memberCount>1) → Drill-in. */
  onStackOpen?: (stackKey: string) => void;
  /** Infinite-Scroll (nur Library). Weglassen → kein Prefetch. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Roher `?q=`-String fürs Treffer-Highlighting im Namen (String-Primitive). */
  highlightQuery?: string;
  /** ⭐-Favoriten-Affordanz in der Namensspalte (Roadmap 3a Phase 1) — nur Library. */
  enableFavorite?: boolean;
};

function isStackRow(a: AssetListItem): boolean {
  return a.stackKey != null && (a.memberCount ?? 1) > 1;
}

export function AssetTable({
  assets,
  sort,
  dir,
  onSortChange,
  density,
  onStackOpen,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  highlightQuery,
  enableFavorite = false,
}: Props) {
  const [, navigate] = useLocation();
  const selection = useSelection();
  const scrollRef = useScrollContainerRef();
  const tableRef = useRef<HTMLDivElement>(null);
  const allIds = useMemo(() => assets.map((a) => a.id), [assets]);
  const selectionActive = selection.count > 0;

  // Zeilen-Virtualizer (ein Item = eine Zeile). Spacer-Row-Pattern unten behält
  // das native <table> + sticky <thead> (im Gegensatz zum absoluten Grid).
  const rowVirtualizer = useVirtualizer({
    count: assets.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: TABLE_OVERSCAN,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const lastRowIndex = virtualRows.length > 0 ? virtualRows[virtualRows.length - 1].index : -1;
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

  // Prefetch (C1, Item-Space hier): synchroner In-Flight-Guard im Hook (C2)
  // schützt gegen Doppel-Fetch bei Mehrfach-Feuern.
  useEffect(() => {
    // Sichtbarkeits-Gate (Session 23): Grid + Tabelle sind permanent gemountet
    // (CSS-Swap). Eine display:none-Tabelle kann ihre Viewport-Höhe NICHT messen
    // → der Virtualizer degeneriert lastRowIndex ~ assets.length-1 → diese
    // Bedingung wäre IMMER true → Runaway-Autoload-Burst (die HIDDEN Tabelle
    // paginierte beim Öffnen der Grid-Ansicht ~80 Seiten nach, prod-Trace + Repro).
    // offsetHeight === 0 ⟺ display:none (self/ancestor); im Test mockt der
    // VirtualizerSeam offsetHeight für Nicht-Root-Elemente auf >0 → Gate transparent.
    if ((tableRef.current?.offsetHeight ?? 0) === 0) return;
    if (
      onLoadMore &&
      hasMore &&
      !loadingMore &&
      lastRowIndex >= 0 &&
      lastRowIndex >= assets.length - PREFETCH_THRESHOLD_ROWS
    ) {
      onLoadMore();
    }
  }, [lastRowIndex, assets.length, hasMore, loadingMore, onLoadMore]);

  // Stapel-Zeile selektiert alle (gefilterten) memberIds; Einzel-Asset die ID.
  function rowSelected(a: AssetListItem): boolean {
    if (isStackRow(a)) {
      const ids = a.memberIds;
      return !a.bulkSelectCapped && (ids?.length ?? 0) > 0 && ids!.every((id) => selection.has(id));
    }
    return selection.has(a.id);
  }
  function applyRowSelection(a: AssetListItem, range: boolean): void {
    if (isStackRow(a)) {
      const ids = a.memberIds;
      if (!a.bulkSelectCapped && ids && ids.length > 0) selection.setMany(ids, !rowSelected(a));
      return;
    }
    if (range && allIds.length > 0) selection.selectRange(a.id, allIds);
    else selection.toggle(a.id);
  }
  // Select-All-Header-State: nutzt `rowSelected` (stapel-aware) statt nur
  // `has(a.id)` — sonst wäre der Header im Stapel-Modus inkonsistent mit den
  // Row-Checkboxen (die alle memberIds selektieren). Select-All sammelt alle
  // selektierbaren IDs (Stapel → memberIds, Einzel → id; capped Stapel → leer).
  const allSelectableIds = useMemo(
    () => assets.flatMap((a) => (isStackRow(a) ? (a.memberIds ?? []) : [a.id])),
    [assets],
  );
  const allSelected = assets.length > 0 && assets.every((a) => rowSelected(a));
  const someSelected = !allSelected && assets.some((a) => rowSelected(a));

  // iOS-Photos Click-Pattern (analog AssetCard, bewusste Duplikation).
  function handleRowActivate(
    asset: AssetListItem,
    e: React.MouseEvent | React.KeyboardEvent,
  ): void {
    if (e.shiftKey) {
      e.preventDefault();
      applyRowSelection(asset, true);
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      applyRowSelection(asset, false);
      return;
    }
    if (selectionActive) {
      e.preventDefault();
      applyRowSelection(asset, false);
      return;
    }
    e.preventDefault();
    if (isStackRow(asset) && asset.stackKey && onStackOpen) {
      onStackOpen(asset.stackKey);
      return;
    }
    navigate(`/assets/${asset.id}`);
  }

  function handleKeyDown(
    asset: AssetListItem,
    e: React.KeyboardEvent<HTMLTableRowElement>,
  ): void {
    if (e.key === 'Enter' || e.key === ' ') {
      handleRowActivate(asset, e);
    }
  }

  // Click-on-active = flip dir (asc↔desc). Click-on-inactive = activate
  // mit Default-Direction der Spalte.
  function handleHeaderClick(key: SortKey): void {
    if (sort === key) {
      onSortChange(key, dir === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(key, defaultDirFor(key));
    }
  }

  // Was würde der NÄCHSTE Click ergeben? Für aria-label-Vorschau.
  function nextDirFor(key: SortKey): SortDir {
    if (sort === key) return dir === 'asc' ? 'desc' : 'asc';
    return defaultDirFor(key);
  }

  function ariaSortFor(key: SortKey): 'ascending' | 'descending' | 'none' {
    if (sort !== key) return 'none';
    return dir === 'asc' ? 'ascending' : 'descending';
  }

  function nextLabel(key: SortKey, columnLabel: string): string {
    const nd = nextDirFor(key);
    return `Nach ${columnLabel} sortieren, ${nd === 'asc' ? 'aufsteigend' : 'absteigend'}`;
  }

  // Direction-Icon nur auf aktiver Spalte; ▲ (chevron up) für asc,
  // ▼ (chevron down) für desc.
  function DirIcon({ d }: { d: SortDir }) {
    return d === 'asc' ? <I.chevU size={10} /> : <I.chevD size={10} />;
  }

  function SortableHeaderButton({ keyName, label }: { keyName: SortKey; label: string }) {
    const active = sort === keyName;
    return (
      <button
        type="button"
        onClick={() => handleHeaderClick(keyName)}
        aria-label={nextLabel(keyName, label)}
        className={`inline-flex items-center gap-1 hover:text-brand-green-700 focus:outline-none focus-visible:underline ${
          active ? 'text-brand-green-700' : ''
        }`}
      >
        {label}
        {active && <DirIcon d={dir} />}
      </button>
    );
  }

  const thBase =
    'sticky top-0 z-10 bg-brand-cream border-b border-ink-200 text-left text-xs2 font-semibold uppercase tracking-[0.03em] text-ink-500 px-3 py-2.5';
  const cellPadding = `px-3 ${ROW_PADDING[density]}`;
  const thumbBase = `${THUMB_SIZE[density]} rounded overflow-hidden bg-ink-100 flex items-center justify-center`;

  // Compact-Mode: Tags-Cells zeigen single-line mit ellipsis statt
  // wrap. title-Tooltip bleibt das Single-Source-of-Truth für vollständige
  // Liste (existing pattern aus regular mode mit `+N`).
  const tagsCellClass =
    density === 'compact'
      ? 'flex flex-nowrap items-center gap-1 overflow-hidden'
      : 'flex flex-wrap items-center gap-1';

  return (
    // Kein `overflow-x-auto` mehr: das würde (CSS-Quirk: x:auto ⇒ y:auto) einen
    // EIGENEN vertikalen Scroll-Container erzeugen → sticky <thead> würde am
    // Div statt am <main> kleben + der Virtualizer (getScrollElement=main)
    // verlöre den Scroll-Bezug. Die Listenansicht läuft nur ≥md, wo die Tabelle
    // passt (Name truncated, Tags overflow `+N`).
    <div ref={tableRef} className="rounded-lg border border-ink-200 bg-white shadow-soft">
      <table className="w-full text-sm" aria-label="Asset-Liste">
        <thead>
          <tr>
            <th scope="col" className={thBase} style={{ width: 44 }}>
              <input
                type="checkbox"
                aria-label={allSelected ? 'Alle abwählen' : 'Alle auswählen'}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={() => selection.setMany(allSelectableIds, !allSelected)}
                className="h-4 w-4 align-middle accent-brand-green-700 cursor-pointer"
              />
            </th>
            <th scope="col" className={thBase} style={{ width: 56 }}>
              <span className="sr-only">Vorschau</span>
            </th>
            <th scope="col" aria-sort={ariaSortFor('name')} className={`${thBase} cursor-pointer`}>
              <SortableHeaderButton keyName="name" label="Name" />
            </th>
            <th scope="col" aria-sort={ariaSortFor('type')} className={`${thBase} cursor-pointer`} style={{ width: 92 }}>
              <SortableHeaderButton keyName="type" label="Type" />
            </th>
            <th scope="col" aria-sort={ariaSortFor('size')} className={`${thBase} cursor-pointer`} style={{ width: 92 }}>
              <SortableHeaderButton keyName="size" label="Größe" />
            </th>
            <th scope="col" className={thBase}>
              Tags
            </th>
            <th scope="col" aria-sort={ariaSortFor('recent')} className={`${thBase} cursor-pointer`} style={{ width: 132 }}>
              <SortableHeaderButton keyName="recent" label="Datum" />
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Spacer-Row trägt die Höhe der ausgeblendeten Zeilen ÜBER dem
              Fenster — behält native <table>-Semantik + sticky <thead>. */}
          {paddingTop > 0 && (
            <tr aria-hidden="true">
              <td colSpan={TABLE_COLSPAN} style={{ height: paddingTop, padding: 0, border: 0 }} />
            </tr>
          )}
          {virtualRows.map((vr) => {
            const asset = assets[vr.index];
            const isSelected = rowSelected(asset);
            const isStack = isStackRow(asset);
            const badge = extBadge(asset.displayFilename, asset.mimeType);
            const tagsView = formatTagsOverflow(asset.tags, 3);
            const fallback = FALLBACK_BG[asset.assetType];

            return (
              <tr
                key={asset.id}
                data-index={vr.index}
                ref={rowVirtualizer.measureElement}
                tabIndex={0}
                aria-selected={selectionActive ? isSelected : undefined}
                onClick={(e) => handleRowActivate(asset, e)}
                onKeyDown={(e) => handleKeyDown(asset, e)}
                className={`group cursor-pointer border-b border-ink-100 last:border-b-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-green-700 ${
                  isSelected
                    ? 'bg-brand-green-700/[0.06] border-l-2 border-l-[#0d5626]'
                    : 'hover:bg-brand-cream'
                }`}
              >
                <td
                  className={`${cellPadding} align-middle`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    aria-label={
                      isSelected
                        ? `„${asset.displayFilename}" abwählen`
                        : `„${asset.displayFilename}" auswählen`
                    }
                    checked={isSelected}
                    // Shift-Klick = Range; das Click-Event trägt shiftKey
                    // zuverlässig (das synthetische onChange nicht). onChange
                    // bleibt no-op, damit die controlled checkbox keine
                    // React-Warning wirft.
                    onChange={() => {}}
                    onClick={(e) => applyRowSelection(asset, e.shiftKey)}
                    className="h-4 w-4 align-middle accent-brand-green-700 cursor-pointer"
                  />
                </td>
                <td className={`${cellPadding} align-middle`}>
                  <div className={thumbBase}>
                    {asset.thumbUrl ? (
                      <img
                        src={asset.thumbUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className={`w-full h-full flex items-center justify-center font-mono font-bold ${THUMB_FONT_SIZE[density]}`}
                        style={{ background: fallback.bg, color: fallback.fg }}
                      >
                        {badge}
                      </div>
                    )}
                  </div>
                </td>
                <td className={`${cellPadding} align-middle`}>
                  <div className="flex items-center gap-2 max-w-[280px]">
                    {/* ⭐-Favoriten-Toggle (Roadmap 3a Phase 1) — in der bestehenden
                        Name-Zelle (kein COLSPAN-Churn). Stapel-Zeilen → kein Stern. */}
                    {enableFavorite && !isStack && (
                      <FavoriteStarButton assetId={asset.id} favorited={asset.favorited} variant="row" />
                    )}
                    <div className="text-xs font-semibold text-brand-green-700 truncate leading-tight" title={asset.displayFilename}>
                      <HighlightedText
                        text={isStack ? asset.stackLabel ?? asset.displayFilename : asset.displayFilename}
                        query={highlightQuery}
                      />
                    </div>
                    {isStack && (
                      <span className="shrink-0 inline-flex items-center gap-1 rounded bg-brand-green-700 px-1.5 py-0.5 text-2xs font-semibold text-brand-cream">
                        <I.stack size={10} />
                        {asset.memberCount}
                      </span>
                    )}
                  </div>
                  {density !== 'compact' && (
                    <div className="text-xs2 text-brand-green-600 truncate max-w-[280px]">
                      {asset.uploaderName ?? '—'}
                    </div>
                  )}
                </td>
                <td className={`${cellPadding} align-middle`}>
                  <span className="inline-block px-2 py-0.5 bg-ink-100 text-brand-green-800 rounded text-xs2 font-mono uppercase">
                    {TYPE_LABEL[asset.assetType]}
                  </span>
                </td>
                <td className={`${cellPadding} align-middle text-brand-green-800 font-mono text-xs`}>
                  {formatSize(asset.fileSize)}
                </td>
                <td className={`${cellPadding} align-middle`}>
                  {asset.tags.length === 0 ? (
                    <span className="text-brand-green-600">—</span>
                  ) : (
                    <span className={tagsCellClass} title={density === 'compact' ? asset.tags.join(', ') : undefined}>
                      {tagsView.visible.map((tag) => (
                        <span
                          key={tag}
                          className="inline-block px-1.5 py-0.5 bg-ink-100 text-brand-green-800 rounded text-xs2 shrink-0"
                        >
                          {tag}
                        </span>
                      ))}
                      {tagsView.remaining > 0 && (
                        <span
                          className="inline-block px-1.5 py-0.5 bg-ink-100 text-brand-green-700 rounded text-xs2 shrink-0"
                          title={asset.tags.join(', ')}
                        >
                          +{tagsView.remaining}
                        </span>
                      )}
                    </span>
                  )}
                </td>
                <td className={`${cellPadding} align-middle text-brand-green-700 font-mono text-xs whitespace-nowrap`}>
                  {formatRelative(asset.createdAt)}
                </td>
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr aria-hidden="true">
              <td colSpan={TABLE_COLSPAN} style={{ height: paddingBottom, padding: 0, border: 0 }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
